import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HarmonyLogo } from '../components/HarmonyLogo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from '../components/ui/input-otp';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Loader2, AlertCircle, ShieldCheck, ArrowLeft, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
// WebAuthn client — imports from a wrapper module so the "stub vs real
// library" switch happens in ONE file (../lib/webauthn.js) instead of
// every consumer. See that file for the swap procedure.
import { startAuthentication, browserSupportsWebAuthnAutofill, isPasskeyClientAvailable } from '../lib/webauthn';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, complete2FALogin, loading: authLoading } = useAuth();

  // Where to go after signing in: the page ProtectedRoute sent us here from
  // (a shared portal link, say), else the dashboard.
  const from = location.state?.from;
  const afterLogin = from?.pathname && from.pathname !== '/login'
    ? `${from.pathname}${from.search || ''}${from.hash || ''}`
    : '/dashboard';
  
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 2FA state
  const [step, setStep] = useState('credentials'); // 'credentials' | 'otp'
  const [otpCode, setOtpCode] = useState('');
  // Phase B: default the "Trust this browser" checkbox to checked —
  // most users want the convenience; those who don't (e.g. logging in
  // from a shared / public machine) can uncheck. Backend also defaults
  // remember_device to True if the field is missing, matching this UX.
  const [rememberDevice, setRememberDevice] = useState(true);
  const [tempToken, setTempToken] = useState(null);
  const [pendingUser, setPendingUser] = useState(null);

  // Conditional-mediation setup. When the browser supports it and the
  // user has any passkey enrolled for this origin, focusing the
  // username input shows those passkeys inline as an autofill option —
  // the user picks one, the OS prompts for biometric, they're in. No
  // button click, no username typing, no password.
  //
  // The abort controller lets us cancel the pending WebAuthn ceremony
  // if the user navigates away or submits the password form instead —
  // otherwise the passkey prompt would keep sitting there in the
  // background and potentially fight the password login.
  const autofillAbortRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const setupAutofill = async () => {
      try {
        if (!(await browserSupportsWebAuthnAutofill())) return;
        const beginRes = await axios.post(`${BACKEND_URL}/api/auth/passkey/login/begin`, {});
        if (cancelled) return;
        const { options, challenge_id } = beginRes.data;
        const controller = new AbortController();
        autofillAbortRef.current = controller;
        // useBrowserAutofill:true tells @simplewebauthn/browser to
        // wire this into the input autofill flow instead of showing a
        // modal immediately. The Promise resolves when the user picks a
        // passkey from the autofill dropdown; it never resolves if
        // they don't.
        const credential = await startAuthentication({
          optionsJSON: options,
          useBrowserAutofill: true,
        });
        if (cancelled) return;
        const finishRes = await axios.post(
          `${BACKEND_URL}/api/auth/passkey/login/finish`,
          { credential, challenge_id },
        );
        const { token, user } = finishRes.data;
        complete2FALogin(token, user);
        toast.success('Signed in with passkey', {
          description: `Welcome back, ${user.full_name || user.username}`,
        });
        if (user.is_temporary_password) {
          navigate('/change-password');
        } else {
          navigate(afterLogin);
        }
      } catch (err) {
        // Silent failure mode. Conditional mediation is a nicety, not a
        // primary flow — if the browser doesn't support it, the user
        // has no passkey, or they dismissed the autofill, we just leave
        // the normal password login as the only path forward.
        if (cancelled) return;
      }
    };
    setupAutofill();
    return () => {
      cancelled = true;
      if (autofillAbortRef.current) {
        try { autofillAbortRef.current.abort(); } catch (_) {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username.trim() || !formData.password.trim()) {
      setError('Please enter both username and password');
      return;
    }
    
    setLoading(true);
    setError('');
    
    const result = await login(formData.username, formData.password);
    setLoading(false);
    
    if (!result.success) {
      setError(result.error);
      return;
    }

    // 2FA not set up yet — redirect to setup page
    if (result.requires_2fa_setup) {
      navigate('/setup-2fa', { state: { tempToken: result.temp_token, user: result.user } });
      return;
    }

    // 2FA is enabled — show OTP input
    if (result.requires_2fa) {
      setTempToken(result.temp_token);
      setPendingUser(result.user);
      setStep('otp');
      setOtpCode('');
      return;
    }

    // No 2FA — proceed normally
    toast.success('Welcome back!', {
      description: `Logged in as ${result.user.full_name || result.user.username}`
    });
    if (result.user.is_temporary_password) {
      navigate('/change-password');
    } else {
      navigate(afterLogin);
    }
  };

  const handleOTPVerify = async (e) => {
    e?.preventDefault();
    if (otpCode.length !== 6) {
      setError('Please enter the full 6-digit code');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/2fa/verify`, {
        otp_code: otpCode,
        temp_token: tempToken,
        remember_device: rememberDevice,
      });
      
      const { token, user } = response.data;
      complete2FALogin(token, user);
      
      toast.success('Welcome back!', {
        description: `Logged in as ${user.full_name || user.username}`
      });
      
      if (user.is_temporary_password) {
        navigate('/change-password');
      } else {
        navigate(afterLogin);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed. Please try again.');
      setOtpCode('');
    } finally {
      setLoading(false);
    }
  };

  // Phase C: Passkey / WebAuthn login. One-touch biometric sign-in as
  // an alternative to password + 2FA. The user MUST type their username
  // first (we don't ship server-side conditional-mediation yet) so the
  // browser knows which credential to prompt for.
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const handlePasskeyLogin = async () => {
    setError('');
    // Skip the network call entirely when the WebAuthn client isn't
    // installed — the /passkey/login/begin call would just come back
    // 200 and then the ceremony would throw client-side anyway.
    // This also avoids logging spurious 500s from a backend where the
    // `webauthn` Python package isn't yet installed either.
    if (!isPasskeyClientAvailable()) {
      setError('Passkey sign-in is not available in this build. Please sign in with your password.');
      return;
    }
    if (!formData.username.trim()) {
      setError('Enter your username first, then sign in with your passkey.');
      return;
    }
    setPasskeyLoading(true);
    try {
      // 1. Server issues a challenge scoped to this user's credentials.
      const beginRes = await axios.post(`${BACKEND_URL}/api/auth/passkey/login/begin`, {
        username: formData.username.trim(),
      });
      const { options, challenge_id } = beginRes.data;
      // 2. Browser triggers the WebAuthn ceremony — OS prompts for
      // biometric / security-key touch. This is what the user sees.
      const credential = await startAuthentication({ optionsJSON: options });
      // 3. Post the signed assertion back for verification.
      const finishRes = await axios.post(`${BACKEND_URL}/api/auth/passkey/login/finish`, {
        credential,
        challenge_id,
      });
      const { token, user } = finishRes.data;
      complete2FALogin(token, user);
      toast.success('Signed in with passkey', {
        description: `Welcome back, ${user.full_name || user.username}`,
      });
      if (user.is_temporary_password) {
        navigate('/change-password');
      } else {
        navigate(afterLogin);
      }
    } catch (err) {
      // WebAuthn user-canceled surfaces as a DOMException — don't scare
      // them with a red error, just no-op.
      if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') {
        // User dismissed the prompt.
        return;
      }
      if (err?.response?.status === 404) {
        setError('No passkey registered for this account. Sign in with your password and register a passkey from your profile.');
      } else {
        setError(err.response?.data?.detail || err.message || 'Passkey sign-in failed');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setStep('credentials');
    setTempToken(null);
    setPendingUser(null);
    setOtpCode('');
    setError('');
  };

  return (
    <div className="login-split" data-testid="login-page">
      {/* Left Panel - Login Form */}
      <div className="flex flex-col justify-center items-center p-8 lg:p-16 bg-white">
        <div className="w-full max-w-md space-y-8 animate-slide-up">
          {/* Logo and Welcome */}
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <HarmonyLogo size={120} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground tracking-tight">
                Welcome to Harmony Apps
              </h1>
              <p className="mt-2 text-muted-foreground">
                {step === 'otp' ? 'Two-factor authentication required' : 'Sign in to access your applications'}
              </p>
            </div>
          </div>

          {/* Credentials Step */}
          {step === 'credentials' && (
            <Card className="border-0 shadow-lg">
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-xl">Sign In</CardTitle>
                <CardDescription>Enter your credentials to continue</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <Alert variant="destructive" className="animate-slide-down">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="username">Username</Label>
                    <Input
                      id="username"
                      name="username"
                      type="text"
                      placeholder="Enter your username"
                      value={formData.username}
                      onChange={handleChange}
                      className="form-input bg-slate-50"
                      disabled={loading}
                      // "username webauthn" is the WebAuthn conditional-
                      // mediation autocomplete token: browsers with saved
                      // passkeys for this origin show them inline in the
                      // username field autofill dropdown. See the mount-time
                      // startAuthentication call below.
                      autoComplete="username webauthn"
                      data-testid="login-username"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={formData.password}
                        onChange={handleChange}
                        className="form-input bg-slate-50 pr-10"
                        disabled={loading}
                        autoComplete="current-password webauthn"
                        data-testid="login-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        tabIndex={-1}
                        data-testid="toggle-password"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full btn-primary bg-primary hover:bg-primary/90"
                    disabled={loading || passkeyLoading}
                    data-testid="login-submit"
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Signing in...</>
                    ) : 'Sign In'}
                  </Button>

                  {/* Passkey sign-in option. Rendered below the primary
                      submit so password remains the muscle-memory action,
                      but visible from first look so enrolled users don't
                      have to hunt for it. Requires a username above so
                      the server can pick the right allowed-credentials
                      list — we surface that as an error if the field is
                      empty. */}
                  <div className="relative py-2">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">or</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handlePasskeyLogin}
                    disabled={loading || passkeyLoading}
                    data-testid="passkey-signin-btn"
                  >
                    {passkeyLoading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Waiting for passkey...</>
                    ) : (
                      <><KeyRound className="mr-2 h-4 w-4" />Sign in with passkey</>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* OTP Step */}
          {step === 'otp' && (
            <Card className="border-0 shadow-lg">
              <CardHeader className="space-y-1 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-primary/10">
                    <ShieldCheck className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">Verify Identity</CardTitle>
                    <CardDescription>
                      Enter the 6-digit code from your authenticator app
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleOTPVerify} className="space-y-6">
                  {error && (
                    <Alert variant="destructive" className="animate-slide-down">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  {pendingUser && (
                    <p className="text-sm text-muted-foreground text-center">
                      Signing in as <span className="font-medium text-foreground">{pendingUser.full_name || pendingUser.username}</span>
                    </p>
                  )}
                  
                  <div className="flex justify-center" data-testid="otp-input-group">
                    <InputOTP
                      maxLength={6}
                      value={otpCode}
                      onChange={setOtpCode}
                      data-testid="otp-input"
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                      </InputOTPGroup>
                      <InputOTPSeparator />
                      <InputOTPGroup>
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>

                  {/* Trusted device — skip 2FA for 30 days on this browser */}
                  <label
                    className="flex items-start gap-2 text-sm text-muted-foreground cursor-pointer select-none"
                    data-testid="remember-device-label"
                  >
                    <input
                      type="checkbox"
                      checked={rememberDevice}
                      onChange={(e) => setRememberDevice(e.target.checked)}
                      className="mt-0.5"
                      data-testid="remember-device-checkbox"
                    />
                    <span>
                      Trust this browser for 30 days
                      <span className="block text-xs opacity-75 mt-0.5">
                        Skip the 2FA prompt on future sign-ins from this device. Uncheck on public / shared machines.
                      </span>
                    </span>
                  </label>

                  <Button
                    type="submit"
                    className="w-full btn-primary bg-primary hover:bg-primary/90"
                    disabled={loading || otpCode.length !== 6}
                    data-testid="otp-verify-btn"
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verifying...</>
                    ) : 'Verify & Sign In'}
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full text-muted-foreground"
                    onClick={handleBackToLogin}
                    data-testid="back-to-login-btn"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to login
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Footer */}
          <p className="text-center text-sm text-muted-foreground">
            &copy; {new Date().getFullYear()} Harmony. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div 
        className="hidden lg:block login-image-panel"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1770488853538-08474909f4b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmUlMjBicmlnaHR8ZW58MHx8fHwxNzcwNzI3NTM1fDA&ixlib=rb-4.1.0&q=85)'
        }}
        data-testid="login-image-panel"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-primary/20 to-secondary/30" />
        <div className="relative z-10 flex flex-col justify-end h-full p-12 text-white">
          <h2 className="text-3xl font-bold mb-4">Your Enterprise Hub</h2>
          <p className="text-lg opacity-90 max-w-md">
            Access all your business applications from one secure platform. 
            Manage expenses, assets, and more with Harmony Apps.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
